/* Hyper calls this adapter explicitly; unrelated fetch requests are untouched. */
window.KeeneCartGuard = (() => {
  const contexts = new Map();
  let queue = Promise.resolve();
  const root = () => window.Shopify?.routes?.root || window.FoxTheme?.routes?.root_url || '/';
  const strings = () => JSON.parse(document.getElementById('KeeneStrings').textContent);
  const valid = (item) => item?.source && ['food', 'grocery', 'send'].includes(item.service);
  const same = (a, b) => a.source === b.source && a.service === b.service;
  const sendCartAttributeKeys = ['Keene delivery location', 'Keene delivery map', 'Keene delivery landmark', 'Keene delivery address', '_bony_market'];
  const enqueue = (task) => {
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result;
  };
  const read = async (url, config) => {
    const response = await fetch(url, { credentials: 'same-origin', ...config });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || strings().cart_error);
    return data;
  };
  const jsonConfig = (body) => ({ method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const clearSendAttributes = async (cart) => {
    const attributes = Object.fromEntries(sendCartAttributeKeys.filter((key) => cart.attributes?.[key]).map((key) => [key, '']));
    if (!Object.keys(attributes).length) return cart;
    const updated = await read(`${root()}cart/update.js`, jsonConfig({ attributes }));
    if (sendCartAttributeKeys.some((key) => updated.attributes?.[key])) throw new Error(strings().cart_error);
    return updated;
  };
  const response = (body, status = 200) => new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } });
  const register = (element) => {
    const context = { source: element.dataset.source, service: element.dataset.service };
    for (const id of (element.dataset.variants || '').split(',')) contexts.set(id, context);
  };
  customElements.define('keene-product-context', class extends HTMLElement {
    connectedCallback() { register(this); }
  });

  async function cartContext(item) {
    let context = contexts.get(String(item.variant_id || item.id));
    if (!context && item.url) {
      // Liquid section rendering also exposes metafield-only services on legacy carts.
      const url = new URL(item.url, location.origin);
      if (url.origin !== location.origin) throw new Error(strings().cart_error);
      url.search = '?section_id=keene-product-context';
      const result = await fetch(url, { credentials: 'same-origin' });
      if (!result.ok) throw new Error(strings().cart_error);
      const doc = new DOMParser().parseFromString(await result.text(), 'text/html');
      doc.querySelectorAll('keene-product-context').forEach(register);
      context = contexts.get(String(item.variant_id || item.id));
    }
    return { source: item.vendor, service: context?.service || item.properties?.['_Keene service'] };
  }

  function confirm(message, allowClear) {
    return new Promise((resolve) => {
      const dialog = document.getElementById('KeeneSourceDialog');
      const trigger = document.activeElement;
      const clear = dialog.querySelector('[data-keene-clear]');
      const close = dialog.querySelector('[data-keene-close]');
      const error = dialog.querySelector('[data-keene-guard-error]');
      error.textContent = message || '';
      error.hidden = !message;
      clear.hidden = !allowClear;
      dialog.classList.toggle('keene-dialog--no-clear', !allowClear);
      const finish = (choice) => {
        dialog.removeEventListener('close', onClose);
        clear.removeEventListener('click', onClear);
        close.removeEventListener('click', onCancel);
        dialog.close();
        if (trigger?.isConnected) trigger.focus();
        resolve(choice);
      };
      const onClose = () => finish(false);
      const onClear = () => finish(true);
      const onCancel = () => finish(false);
      dialog.addEventListener('close', onClose);
      clear.addEventListener('click', onClear);
      close.addEventListener('click', onCancel);
      dialog.showModal();
    });
  }

  function decode(body) {
    if (!(body instanceof FormData)) return JSON.parse(body);
    const properties = {};
    for (const [key, value] of body) {
      const match = key.match(/^properties\[(.*)\]$/);
      if (match) properties[match[1]] = value;
    }
    return { id: body.get('id'), quantity: Number(body.get('quantity') || 1), properties };
  }

  async function guardedFetch(url, config) {
    const isUpdate = /\/cart\/update(?:\.js)?(?:\?|$)/.test(url);
    const payload = decode(config.body);
    const cart = await read(`${root()}cart.js`, { cache: 'no-store' });
    let requested;
    if (isUpdate) {
      // Hyper quick order and gift wrap use update.js to add absent variants.
      requested = Object.entries(payload.updates || {}).filter(([, quantity]) => Number(quantity) > 0).map(([id, quantity]) => {
        const existing = cart.items.find((item) => item.key === id || String(item.variant_id) === id);
        return { id: existing?.variant_id || id, quantity: Number(quantity), existing, updateKey: id, properties: existing?.properties || {} };
      });
      if (!requested.some((item) => !item.existing || item.quantity > item.existing.quantity)) return fetch(url, config);
    } else requested = payload.items || [payload];

    const next = requested.map((item) => contexts.get(String(item.id)));
    if (!next.length || next.some((context) => !valid(context))) throw new Error(strings().missing_context);
    if (next.some((context) => !same(next[0], context))) throw new Error(strings().mixed_bundle);
    const current = await Promise.all(cart.items.map(cartContext));
    let cleared = false;
    if (current.some((context) => !valid(context) || !same(next[0], context)) || (next[0].service === 'send' && cart.items.length > 0)) {
      if (!await confirm('', true)) {
        if (isUpdate) return response(await read(`${root()}cart/update.js`, jsonConfig({ sections: payload.sections, sections_url: payload.sections_url })));
        return response({ status: 422, description: strings().cancelled }, 422);
      }
      const empty = await read(`${root()}cart/clear.js`, jsonConfig({}));
      document.dispatchEvent(new CustomEvent('keene:cart-updated', { detail: { cart: empty } }));
      // Refresh even if the subsequent add fails (e.g. sold out).
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      cleared = true;
    }
    if (next[0].service === 'send') await clearSendAttributes(cart);
    const items = requested.map((item, index) => ({
      ...item,
      properties: { ...item.properties, '_Keene source': next[index].source, '_Keene service': next[index].service }
    }));
    if (isUpdate) {
      const additions = items.filter((item) => cleared || !item.existing).map(({ id, quantity, properties }) => ({ id, quantity, properties }));
      if (additions.length) await read(`${root()}cart/add.js`, jsonConfig({ items: additions }));
      const updates = { ...payload.updates };
      for (const item of items) if (cleared || !item.existing) delete updates[item.updateKey];
      // Return a cart + bundled sections, exactly as Hyper's update handlers expect.
      return fetch(url, { ...config, body: JSON.stringify({ ...payload, updates: cleared ? {} : updates }) });
    }
    if (config.body instanceof FormData) {
      config.body.set('properties[_Keene source]', next[0].source);
      config.body.set('properties[_Keene service]', next[0].service);
    } else config = { ...config, body: JSON.stringify(payload.items ? { ...payload, items } : { ...payload, properties: items[0].properties }) };
    return fetch(url, config);
  }

  const guarded = (url, config) => enqueue(async () => {
    try { return await guardedFetch(String(url), config); }
    catch (error) {
      const messages = strings();
      const description = [messages.cart_error, messages.missing_context, messages.mixed_bundle].includes(error.message) ? error.message : messages.cart_error;
      await confirm(description, false);
      if (/\/cart\/update/.test(url)) throw error;
      return response({ status: 422, description }, 422);
    }
  });

  // Fallback for ordinary forms/customized built-ins unsupported by the browser.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.keeneSend !== undefined || !/\/cart\/add(?:\.js)?$/.test(new URL(form.action).pathname)) return;
    if (typeof form.handleFormSubmit === 'function') return;
    event.preventDefault();
    if (form.dataset.keeneBusy) return;
    form.dataset.keeneBusy = 'true';
    try {
      const result = await guarded(`${root()}cart/add.js`, { method: 'POST', body: new FormData(form) });
      if (result.ok) location.assign(window.FoxTheme?.routes?.cart_url || `${root()}cart`);
    } finally { delete form.dataset.keeneBusy; }
  }, true);
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.keene-dialog').forEach((dialog) => dialog.addEventListener('keydown', (event) => {
      // Keep Hyper's underlying drawer Escape/Tab handlers out of this modal.
      event.stopPropagation();
      if (event.key === 'Escape') {
        // Native dialog closes on keydown; keyup then targets the restored drawer.
        document.addEventListener('keyup', (release) => {
          if (release.key === 'Escape') release.stopImmediatePropagation();
        }, { capture: true, once: true });
      }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), [tabindex="0"]')].filter((element) => element.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }));
  });
  return { fetch: guarded, enqueue, read, jsonConfig, root, strings, clearSendAttributes };
})();
